/**
 * Inventory API Module - Product and Category Management
 * Extracted from monolithic silo-api.ts for modular architecture
 */

import { type RequestFunction } from './types'
import type {
  InventoryItem,
  ProductCreateInput,
  ProductUpdateInput,
  ProductType,
  Category,
  ProductDetail,
  PaginatedResponse,
  FilterParams,
  ProductRecommendation,
  BatchActivity,
  ProductionDashboard,
  MaterialTransfer,
  MaterialTransferTracking,
  ProductTerpene,
  ProductCannabinoid,
  Terpene,
  Cannabinoid,
} from './types'

export class InventoryApi {
  constructor(private request: RequestFunction) {}

  // ============================================================================
  // PRODUCT MANAGEMENT
  // ============================================================================

  async getProducts(params?: FilterParams): Promise<PaginatedResponse<InventoryItem>> {
    return this.request('/api/inventory/products/', { params })
  }

  async getProduct(id: number): Promise<InventoryItem> {
    return this.request(`/api/inventory/products/${id}/`)
  }

  // Enhanced product detail endpoint with lab analysis data
  async getProductDetail(id: number): Promise<ProductDetail> {
    const response = await this.request<any>(`/api/inventory/products/${id}/`)
    
    // Transform and validate the response using our enhanced schema
    return this.transformProductDetailResponse(response)
  }

  async createProduct(data: ProductCreateInput): Promise<InventoryItem> {
    return this.request('/api/inventory/products/', {
      method: 'POST',
      body: JSON.stringify(data)
    })
  }

  async updateProduct(id: number, data: ProductUpdateInput): Promise<InventoryItem> {
    return this.request(`/api/inventory/products/${id}/`, {
      method: 'PATCH',
      body: JSON.stringify(data)
    })
  }

  async deleteProduct(id: number): Promise<void> {
    return this.request(`/api/inventory/products/${id}/archive/`, {
      method: 'PATCH'
    })
  }

  async bulkArchiveProducts(ids: number[]): Promise<void> {
    return this.request('/api/inventory/products/bulk-archive/', {
      method: 'POST',
      body: JSON.stringify({ ids })
    })
  }

  async updateProductQuantity(id: number, quantity: number): Promise<InventoryItem> {
    return this.request(`/api/inventory/products/${id}/update-quantity/`, {
      method: 'PATCH',
      body: JSON.stringify({ quantity })
    })
  }

  async getProductTypes(): Promise<ProductType[]> {
    return this.request('/api/inventory/product-types/')
  }

  // ============================================================================
  // CATEGORY MANAGEMENT (Portal)
  // ============================================================================

  async getCategories(params?: FilterParams): Promise<PaginatedResponse<Category>> {
    return this.request('/api/inventory/categories/', { params })
  }

  async getCategory(id: number): Promise<Category> {
    return this.request(`/api/inventory/categories/${id}/`)
  }

  async createCategory(data: { name: string; description?: string }): Promise<Category> {
    return this.request('/api/inventory/categories/', {
      method: 'POST',
      body: JSON.stringify(data)
    })
  }

  async updateCategory(id: number, data: { name: string; description?: string }): Promise<Category> {
    return this.request(`/api/inventory/categories/${id}/`, {
      method: 'PATCH',
      body: JSON.stringify(data)
    })
  }

  async deleteCategory(id: number): Promise<void> {
    return this.request(`/api/inventory/categories/${id}/`, {
      method: 'DELETE'
    })
  }

  // ============================================================================
  // INVENTORY MANAGEMENT (Admin-specific)
  // ============================================================================

  async getInventoryItems(params?: FilterParams): Promise<PaginatedResponse<InventoryItem>> {
    return this.request('/api/inventory/products/', { params })
  }

  async getInventoryItem(id: number): Promise<InventoryItem> {
    return this.request(`/api/inventory/products/${id}/`)
  }

  async createInventoryItem(data: ProductCreateInput): Promise<InventoryItem> {
    return this.request('/api/inventory/products/', {
      method: 'POST',
      body: JSON.stringify(data)
    })
  }

  async updateInventoryItem(id: number, data: ProductUpdateInput): Promise<InventoryItem> {
    return this.request(`/api/inventory/products/${id}/`, {
      method: 'PATCH',
      body: JSON.stringify(data)
    })
  }

  async deleteInventoryItem(id: number): Promise<void> {
    return this.request(`/api/inventory/products/${id}/`, {
      method: 'DELETE'
    })
  }

  async updateInventoryQuantity(id: number, quantity: number, available_quantity?: number): Promise<InventoryItem> {
    // Use the inventory quantity adjustment endpoint
    return this.request(`/api/inventory/products/${id}/adjust-quantity/`, {
      method: 'POST',
      body: JSON.stringify({ 
        adjustment_type: 'set',
        amount: quantity,
        reason: 'Quantity adjustment from admin interface'
      })
    })
  }

  async getLowStockItems(threshold: number = 10): Promise<InventoryItem[]> {
    // Use the inventory products endpoint with stock status filter
    const response = await this.request<PaginatedResponse<InventoryItem>>('/api/inventory/products/', { 
      params: {
        stock_status: 'low_stock',
        limit: 1000
      }
    })
    return response.results || []
  }

  async bulkArchiveInventory(inventoryIds: number[]): Promise<{ updated_count: number }> {
    return this.request('/api/inventory/products/bulk-update/', {
      method: 'POST',
      body: JSON.stringify({
        product_ids: inventoryIds,
        action: 'archive',
        data: {}
      })
    })
  }

  // ============================================================================
  // UTILITY METHODS
  // ============================================================================

  /**
   * Product transformation utility
   */
  // ============================================================================
  // ADVANCED INVENTORY FEATURES
  // ============================================================================

  /**
   * Advanced product search with full-text search and filters
   * Supports relevance-based or custom sorting
   */
  async advancedProductSearch(searchData: {
    query?: string
    filters?: {
      category?: string
      min_price?: number
      max_price?: number
      in_stock?: boolean
      metadata?: Record<string, any>
    }
    sort_by?: 'relevance' | 'price' | 'name' | 'created' | 'updated'
    page?: number
    per_page?: number
  }): Promise<PaginatedResponse<InventoryItem>> {
    return this.request('/api/inventory/products/advanced-search/', {
      method: 'POST',
      body: JSON.stringify(searchData)
    })
  }

  /**
   * Get smart product recommendations
   * Supports various recommendation algorithms
   */
  async getProductRecommendations(data: {
    product_id?: number
    customer_id?: number
    recommendation_type: 'similar' | 'frequently_bought_together' | 'trending' | 'personalized'
    limit?: number
  }): Promise<ProductRecommendation[]> {
    return this.request('/api/inventory/products/recommendations/', {
      method: 'POST',
      body: JSON.stringify(data)
    })
  }

  /**
   * Get batch product activity stream
   * Returns paginated list of activities from models with ActivityMixin
   */
  async getBatchProductActivityStream(params?: {
    start_date?: string
    end_date?: string
    product_id?: number
    batch_id?: number
    action?: string
    page?: number
    per_page?: number
  }): Promise<PaginatedResponse<BatchActivity>> {
    return this.request('/api/inventory/batch-products/activity-stream/', { params })
  }

  /**
   * Get production runs dashboard metrics
   * Returns aggregated metrics including completion rates and efficiency scores
   */
  async getProductionRunsDashboard(params?: {
    date_from?: string
    date_to?: string
  }): Promise<ProductionDashboard> {
    return this.request('/api/inventory/production-runs/dashboard/', { params })
  }

  // ============================================================================
  // MATERIAL TRANSFERS
  // ============================================================================

  /**
   * Get material transfers list
   */
  async getMaterialTransfers(params?: FilterParams): Promise<PaginatedResponse<MaterialTransfer>> {
    return this.request('/api/inventory/material-transfers/', { params })
  }

  /**
   * Get material transfer tracking information
   */
  async getMaterialTransferTracking(transferId: string): Promise<MaterialTransferTracking> {
    return this.request(`/api/inventory/material-transfers/${transferId}/tracking/`)
  }

  /**
   * Update material transfer tracking information
   */
  async updateMaterialTransferTracking(
    transferId: string, 
    trackingData: Partial<MaterialTransferTracking>
  ): Promise<MaterialTransferTracking> {
    return this.request(`/api/inventory/material-transfers/${transferId}/tracking/`, {
      method: 'PATCH',
      body: JSON.stringify(trackingData)
    })
  }

  // ============================================================================
  // PRODUCT TERPENES MANAGEMENT
  // ============================================================================

  async addProductTerpene(productId: number, data: { terpene_id: number; percentage: string }): Promise<ProductTerpene> {
    return this.request(`/api/inventory/products/${productId}/terpenes/`, {
      method: 'POST',
      body: JSON.stringify(data)
    })
  }

  async updateProductTerpene(productId: number, terpeneId: number, data: { percentage: string }): Promise<ProductTerpene> {
    return this.request(`/api/inventory/products/${productId}/terpenes/${terpeneId}/`, {
      method: 'PATCH',
      body: JSON.stringify(data)
    })
  }

  async deleteProductTerpene(productId: number, terpeneId: number): Promise<void> {
    return this.request(`/api/inventory/products/${productId}/terpenes/${terpeneId}/`, {
      method: 'DELETE'
    })
  }

  // NOTE: These endpoints don't exist - terpenes are extracted from product data
  // async getAvailableTerpenes(): Promise<Terpene[]> {
  //   return this.request('/api/inventory/terpenes/')
  // }

  // ============================================================================
  // PRODUCT CANNABINOIDS MANAGEMENT
  // ============================================================================

  async addProductCannabinoid(productId: number, data: { cannabinoid_id: number; percentage: string }): Promise<ProductCannabinoid> {
    return this.request(`/api/inventory/products/${productId}/cannabinoids/`, {
      method: 'POST',
      body: JSON.stringify(data)
    })
  }

  async updateProductCannabinoid(productId: number, cannabinoidId: number, data: { percentage: string }): Promise<ProductCannabinoid> {
    return this.request(`/api/inventory/products/${productId}/cannabinoids/${cannabinoidId}/`, {
      method: 'PATCH',
      body: JSON.stringify(data)
    })
  }

  async deleteProductCannabinoid(productId: number, cannabinoidId: number): Promise<void> {
    return this.request(`/api/inventory/products/${productId}/cannabinoids/${cannabinoidId}/`, {
      method: 'DELETE'
    })
  }

  // NOTE: These endpoints don't exist - cannabinoids are extracted from product data
  // async getAvailableCannabinoids(): Promise<Cannabinoid[]> {
  //   return this.request('/api/inventory/cannabinoids/')
  // }

  private transformProductDetailResponse(response: any): ProductDetail {
    // Add any transformation logic here if needed
    return response as ProductDetail
  }

  /**
   * Utility method to detect if user is staff based on response fields
   */
  isStaffUser(response: any): boolean {
    // Check for staff-only fields
    return (
      response?.cost !== undefined ||
      response?.margin !== undefined ||
      response?.internal_notes !== undefined ||
      response?.profit !== undefined ||
      response?.supplier_info !== undefined
    )
  }

  /**
   * Helper to detect available fields for role-based UI
   */
  detectAvailableFields(data: any[]): Set<string> {
    const fields = new Set<string>()
    if (data.length > 0) {
      Object.keys(data[0]).forEach(key => fields.add(key))
    }
    return fields
  }
}